import { useTranslation } from "react-i18next";
import { t } from "../../../hooks/useAppTranslation";
import {useContext, useEffect, useMemo, useState} from 'react';

import {DocumentsContext} from '../../../Context/DocumentsContext';
import {
  attachDocumentsToControl,
  getDocumentsCategoriesDict,
  getProjectDocumentsList
} from '../../../Properties/Properties';
import {getServicePromise} from '../../HttpService/HttpService';
import PopUp from '../../PopUp/PopUp';
import {FilePreview} from '../FilePreview/FilePreview';
import '../Documents.scss';

const POPUP_TITLE = 'g-file-linking';
const MODES = {
  categories: 'CATEGORIES',
  documents: 'DOCUMENTS',
};


export const JoinFiles = ({
  controlUuid,
  excludedDocumentsIds = [],
  excludeFromCurrentControl,
  projectUuid,
  onCancel,
  onJoin
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [mode, setMode] = useState(MODES.categories);
  const [initializationOn, setInitializationOn] = useState(true);

  // this one is a selected category id for the next step
  const [categoryId, setCategoryId] = useState(null);
  const [fetchingOn, setFetchingOn] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [documents, setDocuments] = useState([]);

  // this one is for mode DOCUMENTS
  const [checkedDocuments, setCheckedDocuments] = useState([]);

  const { categories, setCategories } = useContext(DocumentsContext);

  useEffect(() => {
    // get categories
    if (categories.length) {
      setInitializationOn(false);
      return
    }
    const callPromise = getServicePromise(getDocumentsCategoriesDict(lang), 'GET');
    callPromise.then(
      function (categories) {
        // category list theoretically may be empty, then only "Wszystkie"
        // category will be displayed on list
        setCategories(categories || []);
        setInitializationOn(false);
      },
      function (error) {
        setFetchError(error);
        setInitializationOn(false);
      }
    );
  }, [lang]);

  useEffect(() => {
    // be careful below - "categoryId" can be 0
    if (categoryId === null) { return }
    setFetchingOn(true);

    const callPromise = getServicePromise(
      getProjectDocumentsList(projectUuid, categoryId), 'GET');
    callPromise.then(
      function (documents_) {
        setDocuments(documents_.filter(document => !!document.file));
        setMode(MODES.documents);
        setFetchingOn(false);
      },
      function (error) {
        setFetchError(error);
        setFetchingOn(false);
      }
    );

  }, [categoryId]);

  const displayedDocuments = useMemo(
    () => {
      let documents_ = documents
		  //note: problem z wyswietlaniem plikow (w szegolach kontroli)
          // .filter(document => !excludedDocumentsIds.includes(document.id))
          .map(document => {
            document.fromCurrentControl = (document.containers || []).map(
                container => container.containerId).includes(controlUuid);
            return document
          });
      // if (excludeFromCurrentControl) {
		  //note: problem z wyswietlaniem plikow pod przyciskiem pliki (lista kontroli)
		  // documents_ = documents_.filter(document => !document.fromCurrentControl);
      // }
      return documents_
    },
    [documents, excludedDocumentsIds, excludeFromCurrentControl]
  );

  const checkedDocumentsIds = useMemo(() => {
    return checkedDocuments.map(document => document.id)
  }, [checkedDocuments]);

  // Error from getting data from server
  if (fetchError !== null) {
    // there was an error when one tried to send request to backend;
    // it was impossible to add file to document or create the new one
    return (
      <PopUp
        notification={fetchError}
        title={t('g-notification')}
        type="error"
        closePopUp={onCancel}
        onClickEvent={onCancel}
      />
    )
  }

  // Mode categories, included fetching and list
  if (mode === MODES.categories) {
    return (
      <PopUp
        customContent
        title={t(POPUP_TITLE)}
        handleClosePopup={onCancel}
      >
        <div className="documents__join-files-container">
          {initializationOn
            ? <p className="info-text" role="status">{ t('g-downloading-category-list') }</p>
            : (
              <>
                <h3 className="sr-only">{ t('g-file-linking-categories') }</h3>
                <div className="documents__join-boxes">
                  {[{entryId: 0, description: t('g-all')}, ...categories].map(categoryData =>
                    <button
                      key={categoryData.entryId}
                      className="btn documents__join-box"
                      disabled={fetchError}
                      type="button"
                      onClick={() => setCategoryId(categoryData.entryId)}
                    >
                      {categoryData.description}
                    </button>
                  )}
                </div>
              </>
            )
          }
        </div>
        <div className="col-12 documents__join-buttons">
          <button
            className="btn delete-btn"
            disabled={fetchError}
            type="button"
            onClick={onCancel}
          >
            { t('g-close') }
          </button>
        </div>
      </PopUp>
    )
  }

  function handleSetCategoriesMode() {
    setMode(MODES.categories);
    setCategoryId(null);
  }

  // Empty files list
  if (!fetchingOn && !displayedDocuments.length) {
    return (
      <PopUp
        notification={t('g-file-list-empty')}
        title={t(POPUP_TITLE)}
        closePopUp={handleSetCategoriesMode}
        onClickEvent={handleSetCategoriesMode}
      />
    )
  }

  function handleCheck(value, document) {
    setCheckedDocuments(prevState => value
      ? [...prevState, document]
      : prevState.filter(doc => doc.id !== document.id)
    );
  }

  function handleSubmit(ev) {
    ev.preventDefault();
    setFetchingOn(true);

    const callPromise = getServicePromise(
      attachDocumentsToControl(
        controlUuid),
      'PUT',
      {documentsToAdd: checkedDocuments.map(doc => doc.uuid)}
    );
    callPromise.then(
      function (documentsData) {
        onJoin(checkedDocuments);
      },
      function (error) {
        setFetchError(error);
        setFetchingOn(false);
      }
    );
  }

  return (
    <PopUp
      customContent
      scrolled
      title={t(POPUP_TITLE)}
      handleClosePopup={fetchingOn ? null : handleSetCategoriesMode}
    >
      <div className="documents__join-files-container">
        {fetchingOn
          ? <p className="info-text" role="status">{ t('external-controls-loading-file-list') }</p>
          : (
            <>
              <h3 className="sr-only">{ t('g-file-linking-list') }</h3>
              <form onSubmit={handleSubmit}>
                <ul className="col-12 documents__join-files-list">
                  {displayedDocuments.map(document => (
                    <li key={document.id} className="row documents__join-files-el">
                      <label className="col-10 documents__join-files-label">
                        <input
                          className="join-files-checkbox"
                          checked={checkedDocumentsIds.includes(document.id)}
                          disabled={fetchingOn}
                          type="checkbox"
                          onChange={ev => handleCheck(ev.target.checked, document)}
                        />
                        <span>{document.file.fileName}</span>
                        {document.fromCurrentControl && <span className="documents__info-text">{ t('g-from-currently-control') }</span>}
                      </label>
                      <FilePreview
                        buttonClassName="col-2"
                        buttonDisabled={fetchingOn}
                        fileData={document.file}
                      />
                    </li>
                  ))}
                </ul>
                <div className="col-12 documents__join-buttons">
                  <button
                    className="btn delete-btn"
                    disabled={fetchingOn}
                    type="button"
                    onClick={handleSetCategoriesMode}
                  >
                    { t('g-close') }
                  </button>
                  <button
                    className="btn save-btn"
                    disabled={fetchingOn || !checkedDocuments.length}
                    type="submit"
                    onClick={handleSubmit}
                  >
                    { t('g-link') }
                  </button>
                </div>
              </form>
            </>
            )
        }
      </div>
    </PopUp>
  )
};
